import React, { FunctionComponent, useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useIntl } from "react-intl";
import { BsBellSlashFill, BsBellFill } from "react-icons/bs";
import {
  Badge,
  Flex,
  Heading,
  IconButton,
  Icon,
  Spinner,
  Stack,
  StackProps,
  Switch,
} from "@chakra-ui/react";
import {
  AddIcon,
  BellIcon,
} from "@chakra-ui/icons";

import { useQuery, useMutation } from "@apollo/client";
import { SITE_ALARM_RULES_QUERY, SITE_ALARM_RULE_ACTIONS_QUERY } from "graphql/queries";
import { SET_ALARM_RULES_MUTATIONS } from "graphql/mutations";
import {
  SiteAlarmRules,
  SiteAlarmRulesVariables,
  SiteAlarmRuleActions,
  SiteAlarmRuleActionsVariables,
  SiteAlarmRules_siteAlarmRules,
  SiteAlarmRules_siteAlarmRules_rules,
  SetAlarmRulesMutation,
  SetAlarmRulesMutationVariables,
  SetAlarmRulesMutation_setAlarmRules,
} from "graphql/generated";

import AlarmRule from "./AlarmRule";


interface IProps extends StackProps {
  alarmType?: string;
  namespace?: string;
  siteId: string;
  textColor: string;
  bgColor: string;
}


const AlarmRules: FunctionComponent<IProps> = ({
  alarmType,
  namespace,
  siteId,
  textColor,
  bgColor,
  ...rest
}) => {
  const intl = useIntl();
  const [alarmRules, setAlarmRules] = useState<SiteAlarmRules_siteAlarmRules | SetAlarmRulesMutation_setAlarmRules | undefined>(undefined);
  const isServerSync = useRef(false);
  const [setAlarmRulesMutation, { loading: saving }] = useMutation<SetAlarmRulesMutation, SetAlarmRulesMutationVariables>(SET_ALARM_RULES_MUTATIONS, { fetchPolicy: "no-cache" });
  const { data, loading: loadingRules } = useQuery<SiteAlarmRules, SiteAlarmRulesVariables>(SITE_ALARM_RULES_QUERY, { variables: { siteId, alarmType, namespace } });
  const { data: dataActions, loading: loadingActions } = useQuery<SiteAlarmRuleActions, SiteAlarmRuleActionsVariables>(SITE_ALARM_RULE_ACTIONS_QUERY, { variables: { siteId } });

  const loading = useMemo<boolean>(() => loadingRules || loadingActions, [loadingRules, loadingActions]);

  useEffect(() => {
    if (isServerSync.current) return;
    isServerSync.current = true;
    setAlarmRules(data?.siteAlarmRules ?? undefined);
  }, [data]);

  useEffect(() => {
    if (isServerSync.current) {
      isServerSync.current = false;
      return;
    }
    if (!alarmRules) return;

    setAlarmRulesMutation({
      variables: {
        siteId,
        alarmType,
        namespace,
        rules: {
          enabled: alarmRules.enabled,
          rules: alarmRules.rules.map(_ => ({
            id: _.id,
            site_id: siteId,
            alarm_type_id: alarmType,
            ns: namespace,
            action_id: _.action.id,
            enabled: _.enabled,
            config: {
              message: _.config.message,
              email: _.config.email,
              phone: _.config.phone,
            },
          })),
        },
      },
      onCompleted: (result) => {
        isServerSync.current = true;
        setAlarmRules(result.setAlarmRules);
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmType, alarmRules]);

  const toggleEnabled = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAlarmRules(prev => prev
      ? {
        ...prev,
        enabled: e.target.checked,
      }
      : prev
    );
  }, []);

  const addRule = useCallback(() => {
    const defaultAction = dataActions?.siteAlarmRuleActions.actions.find(_ => _.type === "NOTIFICATION");

    if (!defaultAction) return;

    setAlarmRules(prev => prev
      ? {
        ...prev,
        rules: [
          {
            enabled: true,
            action: {
              id: defaultAction.id,
              type: defaultAction.type,
            },
            config: {
              message: null,
              email: null,
              phone: null,
            },
          } as SiteAlarmRules_siteAlarmRules_rules,
          ...prev.rules,
        ],
      }
      : prev
    );
  }, [dataActions]);

  return (
    <>

      <Stack direction="column" spacing={3} {...rest}>
        {<>
          <Stack direction="row" alignItems="center">
            <Switch
              title={
                alarmRules?.enabled
                ? intl.formatMessage({
                  id: "alarms__alarm_rules__rules_disable",
                  defaultMessage: "Disable rules",
                })
                : intl.formatMessage({
                  id: "alarms__alarm_rules__rules_enable",
                  defaultMessage: "Enable rules",
                })
              }
              isChecked={alarmRules?.enabled}
              isDisabled={!alarmRules}
              visibility={loading ? "hidden" : "visible"}
              onChange={toggleEnabled}
            />
            <Heading
              flexGrow={1}
            >
              <Icon boxSize={6} as={ alarmRules?.enabled ? BsBellFill : BsBellSlashFill } mr={1} />
              {intl.formatMessage({
                id: "alarms__alarm_rules",
                defaultMessage: "Rules",
              })}
              {
                loading
                ? <Spinner size="sm" ml={2} />
                : <Badge fontSize="sm" bg={textColor} color={bgColor} borderRadius={15} px={2} py={1} ml={2} title={intl.formatMessage({
                  id: "alarms__alarm_rules__rules_number_of",
                  defaultMessage: "Number of defined rules",
                })}>
                  {alarmRules?.rules.length ?? 0}
                </Badge>
              }
              {saving && <Spinner size="xs" ml={2} />}
            </Heading>
            {
              loading || <IconButton
                icon={<AddIcon/>}
                aria-label="Add button"
                title={intl.formatMessage({
                  id: "alarms__alarm_rules__rule_add",
                  defaultMessage: "Add rule",
                })}
                size="sm"
                alignSelf="center"
                onClick={addRule}
              />
            }
          </Stack>
          <Flex direction="column" flexWrap="nowrap" justifyContent="stretch" alignItems="stretch">
            {
              !loading
              && alarmRules?.rules.map((alarm_rule, i) => (
                <AlarmRule
                  key={alarm_rule.id ?? i}
                  rule={alarm_rule}
                  onChange={(updated) => setAlarmRules(prev => prev
                    ? {
                      ...prev,
                      rules: prev.rules.map((_, ii) => ii === i ? updated : _),
                    }
                    : prev
                  )}
                  onRemove={() => setAlarmRules(prev => prev
                    ? {
                      ...prev,
                      rules: prev.rules.filter((_, ii) => ii !== i),
                    }
                    : prev
                  )}
                  actions={dataActions?.siteAlarmRuleActions.actions}
                />
              ))
            }
          </Flex>
        </>}
      </Stack>

    </>
  );
};

export default AlarmRules;
